import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import DashboardPage from "@/pages/Dashboard";
import UseFromAgents from "@/components/UseFromAgents";
import { getDashboard, getFollowingFeed, listSkills } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { WORKSPACE_LINKS } from "@/lib/dashboardNav";
import { domainLabel } from "@/lib/domains";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import type { Dashboard, Skill } from "@/lib/types";

const TRENDING_COUNT = 4;
const FOLLOWING_COUNT = 7; // Following tab shows 7 at first glance, then "See more".

// The workspace sections that render inline on Home via ?section=<key>. "My skills" is intentionally
// absent — it lives on Explore (?tab=mine), so its nav entry links out instead.
type Section = "shared" | "defaults" | "contributions" | "bookmarks" | "suggestions" | "teams" | "archives";
const SECTIONS: Section[] = ["shared", "defaults", "contributions", "bookmarks", "suggestions", "teams", "archives"];

// Desktop right-hand quick nav = the full shared workspace links, "Home" first so the hub is one
// click away from any open section. The mobile menu uses the same list via the header.
const SIDE_NAV = WORKSPACE_LINKS;

// Small stroke icons for the stat cards (16px, currentColor).
const svg = (children: React.ReactNode) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const CARD_ICONS = {
  skills: svg(<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />),
  shared: svg(<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></>),
  defaults: svg(<><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></>),
  contributions: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>),
  bookmarks: svg(<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />),
  suggestions: svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />),
  teams: svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>),
};

// The stat cards shown above the installation card — icon + count, title, and a one-line description.
// Each links to its section; cards with a 0 count are hidden.
const STAT_CARDS: { title: string; desc: string; to: string; icon: React.ReactNode; count: (d: Dashboard) => number }[] = [
  { title: "My skills", desc: "Skills you've published to SkillPatch.", to: "/skills?tab=mine", icon: CARD_ICONS.skills, count: (d) => d.skills.length },
  { title: "Shared with me", desc: "Skills shared with you by a team or grant.", to: "/home?section=shared", icon: CARD_ICONS.shared, count: (d) => d.shared.length },
  { title: "Default skills", desc: "Skills your admin gives every account.", to: "/home?section=defaults", icon: CARD_ICONS.defaults, count: (d) => (d.defaults ?? []).filter((s) => !s.default_hidden).length },
  { title: "Contributions", desc: "Skills you've improved through merged suggestions.", to: "/home?section=contributions", icon: CARD_ICONS.contributions, count: (d) => d.contributions.length },
  { title: "Bookmarks", desc: "Skills you've saved to revisit later.", to: "/home?section=bookmarks", icon: CARD_ICONS.bookmarks, count: (d) => d.bookmarks.length },
  {
    title: "Skill Suggestions",
    desc: "Changes proposed to your skills and by you.",
    to: "/home?section=suggestions",
    icon: CARD_ICONS.suggestions,
    count: (d) =>
      d.suggestions.length + (d.incoming ?? []).filter((s) => s.status !== "merged" && s.status !== "rejected").length,
  },
  { title: "Teams", desc: "Teams you own or belong to.", to: "/home?section=teams", icon: CARD_ICONS.teams, count: (d) => d.teams.length },
];

function timeAgo(iso: string): string {
  // Normalize naive-UTC (no offset) to UTC so elapsed time isn't skewed by the viewer's timezone.
  const norm = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
  const then = new Date(norm).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

function fmtCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k` : String(n);
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

function BoltIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function FeedRow({ s, subtitle }: { s: Skill; subtitle: string }) {
  return (
    <Link
      to={`/skill/${s.slug}`}
      className="group block rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-5 outline-none transition hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary">
          <BoltIcon />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-display text-base font-semibold text-text transition group-hover:text-primary">{s.name}</span>
            {s.layer === 0 && (
              <span className="flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-blue-400">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                OFFICIAL
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-sm text-text/50">{subtitle}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            {typeof s.security_score === "number" && (
              <span className="rounded-md border border-primary/30 px-2 py-1 font-mono text-[11px] font-medium text-primary">SCORE {s.security_score}</span>
            )}
            {s.domains?.[0] && (
              <span className="rounded-md border border-[var(--border)] px-2 py-1 font-mono text-[11px] uppercase tracking-wide text-text/50">{domainLabel(s.domains[0])}</span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-text/45">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              {fmtCount(s.metrics?.downloads ?? 0)} downloads
            </span>
          </div>
        </div>
        <svg className="mt-1 shrink-0 self-center text-text/25 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
      </div>
    </Link>
  );
}

// The static right-hand section nav (desktop only; mobile reaches these via the header menu).
// A glowing vertical rule separates it from the feed.
function SideNav({ active }: { active: Section | null }) {
  return (
    <aside className="relative hidden lg:block lg:pl-10">
      {/* Glowing separator — theme-aware (see .home-divider): a soft warm line in light mode, a
          brighter orange bloom in dark. */}
      <span aria-hidden className="home-divider pointer-events-none absolute left-0 top-0 h-full min-h-[700px] w-px" />
      <nav className="flex flex-col gap-1">
        {SIDE_NAV.map((item) => {
          // "Home" (the hub) is active when no section is open; the rest match their ?section= key.
          const isActive = item.to === "/home" ? active === null : item.section !== null && item.section === active;
          return (
            <Link
              key={item.label}
              to={item.to}
              className={`rounded-lg px-3 py-2 font-display text-[15px] font-semibold transition ${
                isActive ? "bg-primary/10 text-primary" : "text-text hover:bg-white/5 hover:text-primary"
              }`}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

export default function FeedPage() {
  useDocumentTitle("SkillPatch");
  const { user, accessToken, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const sectionParam = params.get("section");
  const section: Section | null = SECTIONS.includes(sectionParam as Section) ? (sectionParam as Section) : null;

  const tab: "foryou" | "following" = params.get("tab") === "following" ? "following" : "foryou";
  const setTab = (t: "foryou" | "following") => setParams(t === "following" ? { tab: "following" } : {}, { replace: true });

  const [trending, setTrending] = useState<Skill[] | null>(null);
  const [following, setFollowing] = useState<Skill[] | null>(null);
  const [dash, setDash] = useState<Dashboard | null>(null);
  // Following list starts collapsed to FOLLOWING_COUNT; "See more" expands it. Resets on tab change.
  const [followingExpanded, setFollowingExpanded] = useState(false);

  useEffect(() => { setFollowingExpanded(false); }, [tab]);

  useEffect(() => {
    listSkills({ collection: "trending", limit: 12 }, accessToken)
      .then((s) => setTrending(s))
      .catch(() => setTrending([]));
  }, [accessToken]);

  useEffect(() => {
    if (tab !== "following" || !accessToken) return;
    if (following !== null) return; // fetch once
    getFollowingFeed(accessToken).then(setFollowing).catch(() => setFollowing([]));
  }, [tab, accessToken, following]);

  // Dashboard counts back the stat cards (logged-in only).
  useEffect(() => {
    if (!accessToken) return;
    getDashboard(accessToken).then(setDash).catch(() => setDash(null));
  }, [accessToken]);

  // Sections are for signed-in users only; send guests to log in and back.
  if (section && !authLoading && !user) {
    return <Navigate to={`/login?next=${encodeURIComponent(`/home?section=${section}`)}`} replace />;
  }

  const name = user?.display_name || user?.username;

  return (
    <main className="w-full px-5 py-10 sm:px-8 lg:pl-16 lg:pr-28">
      {/* Personal greeting — bold, only on the Home hub (not when a workspace section is open). */}
      {user && !section && (
        <h1 className="mb-8 font-display text-2xl font-bold tracking-tight sm:text-3xl">
          Welcome back, {name}.
        </h1>
      )}

      {/* items-start so the right column (and its divider) sizes to the nav content — a consistent
          length across pages that only grows if the nav does — instead of stretching to the full
          page height. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start lg:gap-10">
        <div className="min-w-0">
          {section ? (
            /* Inline workspace section (Teams, Shared, Defaults, Bookmarks, Suggestions, Contributions, Archives). */
            <DashboardPage />
          ) : (
            <>
              {/* Stat cards — the workspace numbers (icon + count, title, description). Cards with a
                  0 count are hidden; the whole block hides when nothing has a count. */}
              {user && dash && STAT_CARDS.some((st) => st.count(dash) > 0) && (
                <div className="mb-10">
                  <Eyebrow>Your account</Eyebrow>
                  {/* Equal columns filling the width so the cards row matches the width of the
                      installation card below it. */}
                  <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
                    {STAT_CARDS.filter((st) => st.count(dash) > 0).map((st) => (
                      <Link
                        key={st.title}
                        to={st.to}
                        className="group flex items-start gap-5 rounded-2xl border border-[var(--border)] bg-[var(--glass)] p-4 transition hover:-translate-y-0.5 hover:border-primary/50"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] text-text/55 transition-colors group-hover:text-primary">
                          {st.icon}
                        </span>
                        <div className="min-w-0">
                          <div className="text-2xl font-bold leading-none tracking-tight text-text">{st.count(dash)}</div>
                          <p className="mt-1 font-display text-sm font-medium text-text/80">{st.title}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent setup — renders nothing when logged out. */}
              <UseFromAgents className="mb-8" />

              {/* Tabs */}
              <nav className="flex gap-7 border-b border-[var(--border)]">
                {([["foryou", "For You"], ["following", "Following"]] as const).map(([id, label]) => (
                  <button key={id} onClick={() => setTab(id)}
                    className={`relative pb-3 font-display text-sm font-medium transition ${tab === id ? "text-text" : "text-text/50 hover:text-text/80"}`}>
                    {label}
                    {tab === id && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
                  </button>
                ))}
              </nav>

              {tab === "foryou" ? (
                <section className="mt-8">
                  <Eyebrow>Trending</Eyebrow>
                  <div className="mt-4 space-y-3">
                    {trending === null ? (
                      <p className="text-sm text-text/40">Loading…</p>
                    ) : trending.length === 0 ? (
                      <p className="text-sm text-text/40">Nothing trending yet.</p>
                    ) : (
                      trending.slice(0, TRENDING_COUNT).map((s) => <FeedRow key={s.id} s={s} subtitle="Trending this week" />)
                    )}
                  </div>
                  {trending && trending.length > 0 && (
                    <Link to="/skills?tab=trending"
                      className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/[0.06] px-4 py-3 text-sm font-medium text-primary transition hover:bg-primary/10">
                      See more trending →
                    </Link>
                  )}
                </section>
              ) : (
                <section className="mt-8">
                  <Eyebrow>From people you follow</Eyebrow>
                  <div className="mt-4 space-y-3">
                    {authLoading ? (
                      <p className="text-sm text-text/40">Loading…</p>
                    ) : !user ? (
                      <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--glass)] px-6 py-14 text-center">
                        <p className="text-sm text-text/60">Log in to see new skills from people you follow.</p>
                        <Link to={`/login?next=${encodeURIComponent("/home?tab=following")}`} className="mt-4 inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">Log in</Link>
                      </div>
                    ) : following === null ? (
                      <p className="text-sm text-text/40">Loading…</p>
                    ) : following.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--glass)] px-6 py-14 text-center">
                        <p className="text-sm text-text/60">No new skills from people you follow in the last week.</p>
                        <Link to="/skills" className="mt-4 inline-block text-sm font-medium text-primary hover:underline">Explore skills to find people to follow →</Link>
                      </div>
                    ) : (
                      (followingExpanded ? following : following.slice(0, FOLLOWING_COUNT)).map((s) => (
                        <FeedRow key={s.id} s={s} subtitle={`New · ${timeAgo(s.created_at)}${s.owner_name ? ` · by ${s.owner_name}` : ""}`} />
                      ))
                    )}
                  </div>
                  {following && !followingExpanded && following.length > FOLLOWING_COUNT && (
                    <button onClick={() => setFollowingExpanded(true)}
                      className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/[0.06] px-4 py-3 text-sm font-medium text-primary transition hover:bg-primary/10">
                      See more
                    </button>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* Static right-hand section nav (signed-in only). */}
        {user && <SideNav active={section} />}
      </div>
    </main>
  );
}
